import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import type { TokensUsageLast30Days } from '../../types/dashboard';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

interface TokensUsage30DaysChartProps {
  data: TokensUsageLast30Days;
}

export default function TokensUsage30DaysChart({ data }: TokensUsage30DaysChartProps) {
  const formatDateWithoutTimezone = (dateString: string): string => {
    const date = new Date(dateString);
    const day = date.getUTCDate().toString().padStart(2, '0');
    const month = (date.getUTCMonth() + 1).toString().padStart(2, '0');
    return `${day}/${month}`;
  };

  const formatTokens = (tokens: number): string => {
    return `${tokens}M`;
  };

  const chartData = {
    labels: data.data.map(item => formatDateWithoutTimezone(item.day_date)),
    datasets: [
      {
        label: 'Tokens de Entrada',
        data: data.data.map(item => item.input_tokens),
        borderColor: 'rgb(34, 197, 94)',
        backgroundColor: 'rgba(34, 197, 94, 0.1)',
        tension: 0.4,
      },
      {
        label: 'Tokens de Saída',
        data: data.data.map(item => item.output_tokens),
        borderColor: 'rgb(249, 115, 22)',
        backgroundColor: 'rgba(249, 115, 22, 0.1)',
        tension: 0.4,
      },
      {
        label: 'Total',
        data: data.data.map(item => item.total_tokens),
        borderColor: 'rgb(99, 102, 241)',
        backgroundColor: 'rgba(99, 102, 241, 0.1)',
        tension: 0.4,
        borderWidth: 2,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'top' as const,
      },
      tooltip: {
        callbacks: {
          label: (context: any) => {
            const value = context.parsed.y;
            return `${context.dataset.label}: ${formatTokens(value)} tokens`;
          },
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: (value: any) => formatTokens(value),
        },
      },
    },
  };

  const getTotalFormatted = (type: 'input' | 'output' | 'total'): string => {
    const total = data.data.reduce((sum, item) => {
      let value = 0;
      if (type === 'input') value = item.input_tokens;
      else if (type === 'output') value = item.output_tokens;
      else value = item.total_tokens;

      return sum + value;
    }, 0);

    return formatTokens(total);
  };

  return (
    <div>
      <div className="grid grid-cols-3 gap-4 mb-4">
        <div className="bg-green-50 dark:bg-green-900/20 p-3 rounded-lg">
          <div className="text-sm text-green-600 dark:text-green-400">Entrada</div>
          <div className="text-lg font-semibold text-green-900 dark:text-green-100">
            {getTotalFormatted('input')}
          </div>
        </div>
        <div className="bg-orange-50 dark:bg-orange-900/20 p-3 rounded-lg">
          <div className="text-sm text-orange-600 dark:text-orange-400">Saída</div>
          <div className="text-lg font-semibold text-orange-900 dark:text-orange-100">
            {getTotalFormatted('output')}
          </div>
        </div>
        <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg">
          <div className="text-sm text-blue-600 dark:text-blue-400">Total</div>
          <div className="text-lg font-semibold text-blue-900 dark:text-blue-100">
            {getTotalFormatted('total')}
          </div>
        </div>
      </div>
      <div className="h-80">
        <Line data={chartData} options={options} />
      </div>
    </div>
  );
}
